import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { REWARD_TARGET_ATTRIBUTE, RewardTarget } from "./rewardTargets";
import { SparkIcon, StarIcon } from "../../../components/StarIcon";
import { playSound } from "../../../utils/sfx";
import { PayoutBuffer } from "../../PayoutBuffer";
import {
  payoutChips,
  RewardChip,
  RewardFlight,
  RewardFlights,
  RewardGlyph,
} from "../../RewardFlights";
import { useGame, useShellVersion, useShopState } from "../../useShell";
import { useTruckStage } from "../trips/TripFade";

/**
 * The payoff moment, ported to the engine shell. A piece selling off the
 * stand is the beat the whole loop builds to, and until this layer
 * existed it was two numbers quietly changing in the top bar.
 *
 * The sim can't animate, so whatever grants a reward launches a flight
 * on the `RewardFlights` entity — a source point in window pixels, the
 * chips to throw, and the readout each one lands on — and this layer
 * stages them: each chip flies from the origin to the element tagged as
 * its target, landing with a thump on it and, for the money, the
 * cha-ching. The sale's burst is one caller (from the middle of the
 * screen, staged from the `PayoutBuffer` once the player is home); the
 * bench's pried nail is another (from the nail's own hole).
 *
 * The underlying numbers changed the instant the reward settled — the
 * flight is decoration over an already-settled state, so nothing here
 * can desync it.
 *
 * The sale flight waits for the player to be home and the truck parked:
 * a sale can land while they're out on a trip, when the readouts the
 * chips aim at aren't on screen, and the arrival roll is still the
 * trip. Nothing is dropped by waiting — the buffer simply keeps until
 * the shop is there to celebrate in.
 */

const FLIGHT_MS = 900;
/** A chip's box (h-7 / w-7): it is centred on its origin and lands
 * centred on its target. */
const CHIP_PX = 28;

/**
 * A pried nail, drawn the way the bench's pry view draws it as it rises
 * out of the hole (`BenchPryView`: a steel shank with a darker head),
 * and tilted the same way — along the claw's lever, head leading — so
 * the chip that takes off is the nail that just came out of the wood.
 */
const PriedNail: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-6 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]"
    aria-hidden="true"
  >
    <g transform="rotate(36 12 12)">
      <line
        x1="12"
        y1="4"
        x2="12"
        y2="21"
        stroke="#c9c3b8"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <circle cx="12" cy="4" r="3.5" fill="#8f8a80" />
    </g>
  </svg>
);

/** What each glyph is drawn as. The star and spark are drawn (see
 * StarIcon) — as literal glyphs they came from whatever system font the
 * browser fell back to, which put a differently-shaped mark in flight
 * than the one waiting on the readout it lands on. */
const GLYPHS: Record<
  RewardGlyph,
  { readonly node: React.ReactNode; readonly className: string }
> = {
  coin: {
    node: "$",
    className:
      "bg-gold text-ink-black border-gold-dark shadow-[0_0_12px_rgba(201,165,92,0.7)]",
  },
  star: {
    node: <StarIcon />,
    className:
      "bg-paper-ivory text-gold-dark border-gold-dark shadow-[0_0_12px_rgba(156,126,63,0.6)]",
  },
  spark: {
    node: <SparkIcon />,
    className:
      "bg-ink-blue text-paper-ivory border-ink-blue shadow-[0_0_12px_rgba(31,58,110,0.7)]",
  },
  nail: {
    // The very nail the pull lifted out of the pallet, bare — no disc
    // around it, so what lands on the tally is the thing that left the
    // wood, not a box of them.
    node: <PriedNail />,
    className: "border-transparent bg-transparent",
  },
};

export const RewardFlightLayer: React.FC = () => {
  const game = useGame();
  useShellVersion();
  const gameState = useShopState();
  const flights = game.entities.tryGetSingleton(RewardFlights);
  // Home and interactive: the shop is on screen, the truck is in the
  // driveway, and the readouts the chips aim at are where they'll land.
  const home = useTruckStage() === "parked" && !gameState.player.away;

  /**
   * Every payout this session has already been staged from. React is
   * free to re-invoke effects, and a drained announcement is the same
   * announcement, not a second payday — it must not fly twice (#159).
   */
  const staged = useRef(new Set<string>());

  // Drain the buffer once the player is home. Announcements already
  // staged are dropped rather than celebrated again.
  useEffect(() => {
    if (!home || !flights) return;
    const buffer = game.entities.tryGetSingleton(PayoutBuffer);
    if (!buffer || buffer.pending.length === 0) return;
    for (const payout of buffer.drain()) {
      if (staged.current.has(payout.id)) continue;
      staged.current.add(payout.id);
      const chips = payoutChips(payout);
      if (chips.length === 0) continue;
      flights.launch({
        id: payout.id,
        chips,
        // Burst from the middle of the window: the one point guaranteed
        // to be on screen wherever the player is standing.
        originX: window.innerWidth / 2,
        originY: window.innerHeight / 2,
      });
    }
  });

  const land = useCallback(
    (flightId: string) => flights?.land(flightId),
    [flights],
  );
  const finish = useCallback(
    (flightId: string) => flights?.finish(flightId),
    [flights],
  );

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[60] overflow-hidden"
      aria-hidden
      data-testid="reward-flights"
    >
      {flights?.all.map((flight) => (
        <FlightChips
          key={flight.id}
          flight={flight}
          onLand={land}
          onDone={finish}
        />
      ))}
    </div>
  );
};

/** The element a chip lands on: its target, or its fallback when the
 * target isn't mounted (the tally's rows fold away with the tally). */
function targetElement(chip: RewardChip): HTMLElement | null {
  for (const target of [chip.target, chip.fallbackTarget]) {
    if (!target) continue;
    const element = document.querySelector<HTMLElement>(
      `[${REWARD_TARGET_ATTRIBUTE}="${target}"]`,
    );
    if (element) return element;
  }
  return null;
}

/**
 * One flight's chips. Each measures its target once mounted — the
 * readouts move with the layout, so the vector can't be precomputed, and
 * a readout that only appeared with this very reward (the tally's first
 * nail) is in the DOM by the time the layout effect runs, where it
 * wouldn't be during the render — and reports back so the flight can be
 * dropped once the last chip lands.
 */
const FlightChips: React.FC<{
  flight: RewardFlight;
  onLand: (flightId: string) => void;
  onDone: (flightId: string) => void;
}> = ({ flight, onLand, onDone }) => {
  const [legs, setLegs] = useState<ReadonlyArray<{
    readonly target: RewardTarget;
    readonly x: number;
    readonly y: number;
  } | null> | null>(null);

  useLayoutEffect(() => {
    setLegs(
      flight.chips.map((chip) => {
        const element = targetElement(chip);
        if (!element) return null;
        const rect = element.getBoundingClientRect();
        return {
          target: element.getAttribute(REWARD_TARGET_ATTRIBUTE) as RewardTarget,
          x: rect.left + rect.width / 2 - (flight.originX + chip.offsetX),
          y: rect.top + rect.height / 2 - (flight.originY + chip.offsetY),
        };
      }),
    );
  }, [flight]);

  const landed = useRef(new Set<string>());

  // Drop the whole flight once the last chip has finished its arc.
  useEffect(() => {
    const last = flight.chips.reduce(
      (latest, chip) => Math.max(latest, chip.delayMs),
      0,
    );
    const timer = window.setTimeout(
      () => onDone(flight.id),
      last + FLIGHT_MS + 120,
    );
    return () => window.clearTimeout(timer);
  }, [flight, onDone]);

  const handleLanding = (chip: RewardChip, target: RewardTarget) => {
    onLand(flight.id);
    // Chips of a kind land in a stream; only the first one of each pays for
    // a sound and a thump, or a twelve-coin payout would machine-gun.
    if (landed.current.has(target)) return;
    landed.current.add(target);
    if (target === "money") {
      playSound("cash-register", 0.75, "sfx");
    }
    pulseTarget(target);
  };

  if (!legs) return null;
  return (
    <>
      {flight.chips.map((chip, index) => {
        const leg = legs[index];
        if (!leg) return null;
        const glyph = GLYPHS[chip.glyph];
        return (
          <span
            key={chip.id}
            data-reward-glyph={chip.glyph}
            className={`reward-chip absolute flex h-7 w-7 items-center justify-center rounded-full border text-sm font-bold leading-none ${glyph.className}`}
            style={
              {
                left: flight.originX + chip.offsetX - CHIP_PX / 2,
                top: flight.originY + chip.offsetY - CHIP_PX / 2,
                "--fly-x": `${leg.x}px`,
                "--fly-y": `${leg.y}px`,
                "--fly-delay": `${chip.delayMs}ms`,
                "--fly-duration": `${FLIGHT_MS}ms`,
              } as React.CSSProperties
            }
            onAnimationEnd={() => handleLanding(chip, leg.target)}
          >
            {glyph.node}
          </span>
        );
      })}
    </>
  );
};

/** Thump the readout a chip just landed on. */
function pulseTarget(target: RewardTarget): void {
  const element = document.querySelector<HTMLElement>(
    `[${REWARD_TARGET_ATTRIBUTE}="${target}"]`,
  );
  if (!element) return;
  element.classList.remove("reward-pulse");
  // Reading offsetWidth restarts the animation when two payouts land close
  // together; without it the class re-add is coalesced into no change.
  void element.offsetWidth;
  element.classList.add("reward-pulse");
  window.setTimeout(() => element.classList.remove("reward-pulse"), 500);
}
